import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  Eye,
  Award,
  Phone,
  ChevronRight,
  BookOpen,
} from 'lucide-react';
import { Wordmark, Eyebrow, CtaChevrons } from './shared';

const STRUCTURED_DATA = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Pathology Billing Case Studies | Horizon RCM',
  description:
    'Real-world results from Horizon RCM — exclusive pathology billing and revenue cycle management. Read case studies showing how we resolve modifier errors, reduce denials, and drive sustainable net collection rates for pathology laboratories.',
  url: 'https://horizon-rcm.com/case-studies',
  publisher: {
    '@type': 'Organization',
    name: 'Horizon RCM',
    logo: {
      '@type': 'ImageObject',
      url: 'https://horizon-rcm.com/logo512.png',
    },
  },
};

const caseStudies = [
  {
    slug: '/case-studies/pathology-billing-modifier-accuracy',
    eyebrow: 'Independent Pathology Lab · Texas',
    title: 'Reclaiming Revenue and Restoring Trust',
    subtitle:
      'How Horizon RCM resolved chronic Modifier 26 billing errors and transformed collections for an independent pathology practice.',
    stats: [
      { icon: TrendingUp, value: '> 92%', label: 'Net Collection Rate' },
      { icon: Eye, value: 'On-Demand', label: 'Claim Lookup' },
      { icon: Award, value: '100%', label: 'Modifier Accuracy' },
    ],
    tags: ['Modifier 26', 'Professional Component', 'Denial Management', 'Transparency'],
    excerpt:
      'After cycling through multiple billing vendors and accumulating compounding modifier errors, this independent Texas pathology lab needed a complete reset — on accuracy, on accountability, and on trust.',
  },
];

const comingSoon = [
  {
    eyebrow: 'Anatomic Pathology Group',
    title: 'Case Study Coming Soon',
    subtitle: 'How Horizon RCM reduced denial rates and improved net collections for a multi-physician anatomic pathology group.',
  },
  {
    eyebrow: 'Molecular Diagnostics Lab',
    title: 'Case Study Coming Soon',
    subtitle: 'Streamlining CPT coding and payer contracting for a high-volume molecular diagnostics laboratory.',
  },
];

function CaseStudiesIndex() {
  useEffect(() => {
    const previousTitle = document.title;
    const metaDescription = document.querySelector('meta[name="description"]');
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    const previousDescription = metaDescription?.getAttribute('content') ?? '';
    const previousKeywords = metaKeywords?.getAttribute('content') ?? '';
    const previousCanonical = canonicalLink?.getAttribute('href') ?? '';

    document.title = 'Pathology Billing Case Studies | Horizon RCM';

    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Read real-world pathology billing case studies from Horizon RCM. See how we resolve modifier errors, eliminate billing denials, and consistently achieve 92%+ net collection rates for pathology labs.'
      );
    }
    if (metaKeywords) {
      metaKeywords.setAttribute(
        'content',
        'pathology billing case studies, pathology RCM results, pathology billing success stories, modifier 26 pathology billing, pathology lab revenue cycle case study, pathology billing outcomes'
      );
    }
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://horizon-rcm.com/case-studies');
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'case-studies-schema';
    script.textContent = JSON.stringify(STRUCTURED_DATA);
    document.head.appendChild(script);

    return () => {
      document.title = previousTitle;
      if (metaDescription) metaDescription.setAttribute('content', previousDescription);
      if (metaKeywords) metaKeywords.setAttribute('content', previousKeywords);
      if (canonicalLink) canonicalLink.setAttribute('href', previousCanonical);
      const injected = document.getElementById('case-studies-schema');
      if (injected) injected.remove();
    };
  }, []);

  return (
    <div className='min-h-screen bg-brand-cream text-brand-charcoal font-body'>

      {/* Header */}
      <header className='border-b border-brand-coffee/10 bg-brand-warmwhite/95 backdrop-blur-sm sticky top-0 z-50'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between gap-6'>
          <Link to='/' className='text-left'>
            <Wordmark />
          </Link>
          <nav className='hidden md:flex items-center gap-8'>
            <Link
              to='/'
              className='font-body text-sm font-medium uppercase text-brand-coffee hover:text-brand-ember transition-colors'
              style={{ letterSpacing: '0.18em' }}
            >
              Home
            </Link>
            <Link
              to='/#services'
              className='font-body text-sm font-medium uppercase text-brand-coffee hover:text-brand-ember transition-colors'
              style={{ letterSpacing: '0.18em' }}
            >
              Services
            </Link>
            <Link
              to='/#contact'
              className='font-body text-sm font-medium uppercase text-brand-coffee hover:text-brand-ember transition-colors'
              style={{ letterSpacing: '0.18em' }}
            >
              Contact
            </Link>
          </nav>
          <a
            href='tel:+18882576123'
            className='hidden sm:flex items-center gap-2 font-body text-sm font-medium text-brand-coffee hover:text-brand-ember transition-colors'
          >
            <Phone size={16} className='text-brand-ember' />
            (888) 257-6123
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className='bg-brand-navy py-20 sm:py-28'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='max-w-3xl'>
            <Eyebrow className='text-brand-ember mb-5'>Client Success Stories</Eyebrow>
            <h1 className='font-display font-bold text-brand-warmwhite text-5xl sm:text-6xl md:text-7xl leading-[1.05] mb-6'>
              Pathology Billing{' '}
              <em className='text-brand-ember'>Case Studies</em>
            </h1>
            <p className='font-body text-xl text-brand-cream/75 leading-relaxed max-w-2xl'>
              Real results from real pathology practices. Every case study below reflects
              what happens when billing is done with both technical precision and genuine
              accountability — modifier accuracy, denial management, and full transparency.
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <main className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24'>

        <div className='mb-4'>
          <Eyebrow className='text-brand-stone/70'>Published Case Studies</Eyebrow>
        </div>

        {/* Published case studies */}
        <div className='space-y-8 mb-20'>
          {caseStudies.map((study, i) => (
            <article
              key={i}
              className='bg-brand-warmwhite border border-brand-coffee/10 hover:border-brand-ember/40 transition-colors duration-300 group'
            >
              <div className='grid grid-cols-1 lg:grid-cols-12'>

                {/* Content */}
                <div className='lg:col-span-8 p-8 sm:p-10 lg:p-12'>
                  <Eyebrow className='text-brand-ember mb-3'>{study.eyebrow}</Eyebrow>
                  <h2 className='font-display font-bold text-brand-coffee text-3xl sm:text-4xl leading-snug mb-4 group-hover:text-brand-ember transition-colors duration-300'>
                    {study.title}
                  </h2>
                  <p className='font-body text-brand-stone text-lg leading-relaxed mb-5'>
                    {study.subtitle}
                  </p>
                  <p className='font-body text-brand-charcoal/70 leading-relaxed mb-8'>
                    {study.excerpt}
                  </p>

                  {/* Tags */}
                  <div className='flex flex-wrap gap-2 mb-8'>
                    {study.tags.map(tag => (
                      <span
                        key={tag}
                        className='font-body text-[10px] font-semibold uppercase px-3 py-1 bg-brand-cream border border-brand-coffee/15 text-brand-stone'
                        style={{ letterSpacing: '0.15em' }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <Link
                    to={study.slug}
                    className='inline-flex items-center gap-2 font-body font-semibold text-sm uppercase text-brand-ember hover:text-brand-coffee transition-colors duration-300 border-b border-brand-ember hover:border-brand-coffee pb-0.5'
                    style={{ letterSpacing: '0.18em' }}
                    aria-label={`Read full case study: ${study.title}`}
                  >
                    Read Full Case Study
                    <ChevronRight size={16} />
                  </Link>
                </div>

                {/* Stats panel */}
                <div className='lg:col-span-4 bg-brand-navy p-8 sm:p-10 lg:p-12 flex flex-col justify-center gap-8'>
                  {study.stats.map((stat, j) => {
                    const Icon = stat.icon;
                    return (
                      <div key={j} className='flex items-start gap-4'>
                        <Icon size={22} className='text-brand-ember flex-shrink-0 mt-1' />
                        <div>
                          <div className='font-display font-bold text-brand-ember text-3xl leading-none mb-1'>
                            {stat.value}
                          </div>
                          <div
                            className='font-body text-[10px] font-semibold uppercase text-brand-cream/60'
                            style={{ letterSpacing: '0.2em' }}
                          >
                            {stat.label}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

              </div>
            </article>
          ))}
        </div>

        {/* Coming Soon */}
        <div className='mb-8'>
          <Eyebrow className='text-brand-stone/70 mb-8'>More Case Studies Coming Soon</Eyebrow>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            {comingSoon.map((item, i) => (
              <div
                key={i}
                className='bg-brand-warmwhite border border-dashed border-brand-coffee/20 p-8 flex flex-col gap-3'
              >
                <div className='flex items-center gap-2'>
                  <BookOpen size={16} className='text-brand-stone/50' />
                  <Eyebrow className='text-brand-stone/50'>{item.eyebrow}</Eyebrow>
                </div>
                <h3 className='font-display font-bold text-brand-coffee/40 text-2xl'>
                  {item.title}
                </h3>
                <p className='font-body text-brand-stone/50 text-sm leading-relaxed'>
                  {item.subtitle}
                </p>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* CTA */}
      <section className='bg-brand-warmwhite border-t border-brand-coffee/10 py-16'>
        <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
          <Eyebrow className='text-brand-ember mb-4'>Want Results Like These?</Eyebrow>
          <h2 className='font-display font-bold text-brand-coffee text-3xl sm:text-4xl mb-5'>
            Let's talk about your pathology lab.
          </h2>
          <p className='font-body text-lg text-brand-stone leading-relaxed mb-8 max-w-2xl mx-auto'>
            Every practice is different. Share your situation and we'll tell you
            honestly whether Horizon RCM is the right fit — no pressure, no obligation.
          </p>
          <div className='inline-flex items-center'>
            <CtaChevrons side='left' />
            <Link
              to='/consultation'
              className='inline-block bg-brand-ember hover:bg-brand-coffee text-brand-warmwhite font-body font-semibold text-sm uppercase py-4 px-10 transition-colors duration-300'
              style={{ letterSpacing: '0.18em' }}
            >
              Request a Free Consultation
            </Link>
            <CtaChevrons side='right' />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className='bg-brand-navy py-12 border-t-4 border-brand-ember'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-6'>
          <Link to='/'>
            <Wordmark onDark />
          </Link>
          <div className='text-center sm:text-right'>
            <Link
              to='/'
              className='font-body text-sm text-brand-cream/70 hover:text-brand-ember transition-colors'
            >
              Explore the full site →
            </Link>
            <p
              className='font-body text-brand-cream/40 mt-2 text-[10px] font-semibold uppercase'
              style={{ letterSpacing: '0.28em' }}
            >
              Exclusive Pathology Billing · Pathology Revenue Cycle Management · 100% US-Based
            </p>
          </div>
        </div>
        <p className='font-body text-brand-cream/40 text-xs text-center mt-8'>
          © 2025 Horizon RCM Partners LLC. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export default CaseStudiesIndex;
